import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Process EDI 997 Acknowledgement Step
 *
 * Reads a supplier's X12 997 Functional Acknowledgment of the 844 claim
 * batches we sent, stores it, and marks each claim batch it matches as
 * acknowledged, acknowledged with errors, or rejected. An acknowledgement only
 * says the file was readable — it never changes a claim's outcome.
 */
export const processEdi997Step = defineStepConfig({
  type: "process_edi_997",
  name: "Process EDI 997 Acknowledgement",
  description:
    "Parse a supplier's EDI (X12) 997 acknowledgement of our 844 claims, store it, and mark the claim batches it acknowledges.",
  category: "Transformation",
  libraryModule: "ShipAndDebit",
  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The inbound email carrying an EDI 997 (attachment or body)",
    ),
  }),

  outputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "Pass-through of the inbound email",
    ),
    status: z
      .enum([
        "matched",
        "partially_matched",
        "unmatched",
        "duplicate",
        "skipped",
      ])
      .describe(
        "Whether the 997's transaction sets were tied to claim batches",
      ),
    acknowledgementIds: z
      .array(z.string().uuid())
      .describe("IDs of the stored EDI 997 acknowledgements, one per 997"),
    matchedSubmissionIds: z
      .array(z.string().uuid())
      .describe("844 submissions whose acknowledgement status was set"),
    reason: z.string().describe("Explanation of the processing result"),
  }),

  configSchema: z.object({}),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "2 minutes",
    retry: {
      maximumAttempts: 3,
      initialInterval: "30 seconds",
      backoffCoefficient: 2,
      maximumInterval: "2 minutes",
    },
  },
});
